import { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { RegistrationFormData } from "@/app/types/user";
import { Gender } from "@/app/shared_enums/enums";
import { toast } from "@/components/ui/UseToast";
import { Button } from "@/components/ui/Button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/Form";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { PhoneNumberUtil } from "google-libphonenumber";
import { useGetCountriesQuery, useRegisterUserMutation } from "@/services/UserApi";
import PhoneInputField from "@/components/custom-component/PhoneInputField";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateUserInfo } from "@/features/auth/AuthSlice";
const { VITE_ORG_ID } = import.meta.env;
import "@/components/admin/authentication/style.css";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { cn } from "@/lib/utils";
import { Check, ChevronDownIcon } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/Command";
import { CountryTypes } from "@/app/types/shared_types";
export default function RegistrationForm() {
  const [country, setCountry] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();
  const navigate = useNavigate(); // Initialize the navigation hook
  const userPersona = localStorage.getItem("userType");
  const isAuthenticated = Boolean(localStorage.getItem("userToken"));
  const userInfo = JSON.parse(localStorage.getItem("userInfo") ?? "{}");
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      isAuthenticated &&
      userPersona === "RemoteUser" &&
      location.pathname === "/user-login"
    ) {
      navigate("/user/home");
    }
  }, [location]);

  const initialValue = {
    id: userInfo.id as number,
    username: "",
    first_name: "",
    last_name: "",
    email: userInfo.email || "",
    phone_num: "",
    org_id: VITE_ORG_ID,
    country:undefined,
    gender: Gender.MALE,
  };
  const [registerUser] = useRegisterUserMutation();
  const { data: countries } = useGetCountriesQuery();
  const phoneUtil = PhoneNumberUtil.getInstance();

  const validatePhone = (value: string | undefined | null) => {
    value = "+" + value;
    if (!value) {
      return true; // If value is empty, validation passes (based on requirements)
    }

    if (value.length > 20) {
      return "Phone number cannot exceed 20 digits";
    }

    if (value.length <= 4) {
      return true; // Pass validation if length is 5 or fewer
    }

    try {
      // Parse the phone number with the default country code (can be adjusted)
      const parsedNumber = phoneUtil.parseAndKeepRawInput(value);

      // Check if the parsed number is a valid phone number
      if (!phoneUtil.isValidNumber(parsedNumber)) {
        return "Invalid phone number";
      }

      return true; // Return true if validation passes
    } catch (error) {
      // Catch parsing errors and return an appropriate message
      return "Invalid phone number format";
    }
  };

  const form = useForm<RegistrationFormData>({
    mode: "all",
    defaultValues: initialValue,
  });

  const {
    control,
    formState: { errors },
  } = form;

  async function onSubmit(values: RegistrationFormData) {
    setIsSubmitting(true);
    try {
      await registerUser({
        ...values,
      })
        .unwrap()
        .then((res) => {
          const updatedUserInfo = {
            ...userInfo, // Preserve existing user info
            first_name: res.first_name, // Updated first_name from API response
            last_name: res.last_name, // Updated last_name from API response
          };
          // Update localStorage FIRST
          localStorage.setItem("userInfo", JSON.stringify(updatedUserInfo));
          // Optionally, save to localStorage if needed
          dispatch(updateUserInfo(updatedUserInfo)); // Use the correct action name          localStorage.setItem("userInfo", JSON.stringify(updatedUserInfo));
          toast({
            variant: "success",
            description: "Registered Successfully",
          });
          navigate("/user/home");
        })
        .catch((error) => {
          toast({
            variant: "destructive",
            description: `Failed to Register. ${error?.data?.detail || error?.data?.message}`,
          });
        });
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  }

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };
  const watcher=form.watch();
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50 loginpage-image bg-cover bg-center bg-no-repeat overflow-y-auto custom-scrollbar">
      <Card className="w-full max-w-md overflow-hidden rounded-3xl bg-black/40 backdrop-blur-xl backdrop-saturate-150 border-0">
        <CardHeader>
          <CardTitle className="text-2xl text-center text-white">
            Registration Form
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit, onError)}
              className="space-y-6"
            >
              <FormField
                control={form.control}
                name="username"
                rules={{ required: "Required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">ITS No</FormLabel>
                    <FormControl>
                      <Input
                        className="w-full text-white placeholder:text-white p-4 bg-transparent rounded-lg shadow-[inset_0px_4px_6px_rgba(0,0,0,0.2)] border border-gray-300 focus:outline-none"
                        placeholder="Choose a unique username"
                        {...field}
                        max={"40"}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="first_name"
                  rules={{
                    required: "Required",
                    pattern: {
                      value: /^[A-Za-z0-9]+$/,
                      message: "Only letters and numbers are allowed",
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white">First Name</FormLabel>
                      <FormControl>
                        <Input
                          className="w-full text-white placeholder:text-white p-4 bg-transparent rounded-lg shadow-[inset_0px_4px_6px_rgba(0,0,0,0.2)] border border-gray-300 focus:outline-none"
                          placeholder="First Name"
                          {...field}
                          maxLength={40}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  rules={{
                    required: "Required",
                    pattern: {
                      value: /^[A-Za-z0-9]+$/,
                      message: "Only letters and numbers are allowed",
                    },
                  }}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white">Last Name</FormLabel>
                      <FormControl>
                        <Input
                          className="w-full text-white placeholder:text-white p-4 bg-transparent rounded-lg shadow-[inset_0px_4px_6px_rgba(0,0,0,0.2)] border border-gray-300 focus:outline-none"
                          placeholder="Last Name"
                          {...field}
                          maxLength={40}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                rules={{ required: "Required" }}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        className="w-full text-white placeholder:text-white p-4 bg-transparent rounded-lg shadow-[inset_0px_4px_6px_rgba(0,0,0,0.2)] border border-gray-300 focus:outline-none"
                        placeholder="Enter your email"
                        {...field}
                        maxLength={50}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="relative">
                <FormField
                  control={form.control}
                  name="country"
                  rules={{ required: "Required" }}
                  render={({ field, fieldState: { error } }) => (
                    <FormItem className="flex flex-col w-full">
                      <FormLabel className="text-white">Country Name</FormLabel>
                      <div className="flex flex-col w-full">
                        <Popover open={country} onOpenChange={setCountry}>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                role="combobox"
                                className={cn(
                                  "font-normal text-white border-white border-[1px] justify-between hover:bg-transparent",
                                  !field.value && "  focus:border-white "
                                )}
                              >
                                {field.value
                                  ? countries?.find(
                                      (country: CountryTypes) =>
                                        country.id === field.value // Compare with numeric value
                                    )?.country // Display country name if selected
                                  : "Select country"}
                                <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="p-0">
                            <Command>
                              <CommandList>
                                <CommandInput placeholder="Select Country" />
                                <CommandEmpty>No country found.</CommandEmpty>
                                <CommandGroup>
                                  {countries &&
                                    countries.map((country: CountryTypes) => (
                                      <CommandItem
                                        value={country.country}
                                        key={country.id}
                                        onSelect={() => {
                                          form.setValue(
                                            "country",
                                            country.id // Set country_id to country.id as number
                                          );
                                          setCountry(false);
                                        }}
                                      >
                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                            country.id === field.value
                                              ? "opacity-100"
                                              : "opacity-0"
                                          )}
                                        />
                                        {country.country}{" "}
                                      </CommandItem>
                                    ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <FormMessage>{error?.message}</FormMessage>
                    </FormItem>
                  )}
                />
              </div>
              <div className="relative ">
                <FormLabel className="pb-2 text-white">Phone Number</FormLabel>
                <Controller
                  name={"phone_num"}
                  rules={{ required: "Required", validate: validatePhone }}
                  control={control}
                  render={({
                    field: { onChange, value, onBlur },
                    fieldState: { invalid, error },
                  }) => (
                    <PhoneInputField
                      value={value as string}
                      onChange={onChange}
                      inputcolor="#fff"
                      error={errors.phone_num?.message as string}
                      placeholder={"Enter phone number"}
                    />
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="gender"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white">Gender</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full text-white p-4 bg-transparent rounded-lg shadow-[inset_0px_4px_6px_rgba(0,0,0,0.2)] border border-gray-300 focus:outline-none">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={Gender.MALE}>Male</SelectItem>
                        <SelectItem value={Gender.FEMALE}>Female</SelectItem>
                        <SelectItem value={Gender.OTHER}>Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full text-black hover:!bg-primary hover:!text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Registering..." : "Save"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
